const HARNESS_FIELDS = new Set([
  "DIMENSION_FIELD_PROMPT_HARNESS",
  "DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_HARNESS",
]);
const ROW_IDS = new Set([
  "DIMENSION_FIELD_PROMPT_EVENT_ID",
  "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID",
  "DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_TRACE_ID",
]);
const isHarnessField = value => typeof value === "string" && /^DIMENSION_FIELD_.*_HARNESS(?:_LABEL)?$/.test(value);

export function isUnsupportedHarness(error) {
  if (error?.status !== 400) return false;
  const details = `${error?.responseText || ""}\n${error?.message || ""}`;
  return /DIMENSION_FIELD_\w+_HARNESS(?:_LABEL)?\b/.test(details)
    || details.includes("field Unspecified does not belong to entity");
}

export function hasHarnessFields(value) {
  if (Array.isArray(value)) return value.some(hasHarnessFields);
  if (value && typeof value === "object") return Object.values(value).some(hasHarnessFields);
  return isHarnessField(value);
}

// During a mixed-version rollout only row decorations can be omitted. A
// harness predicate, grouping or ordering must keep its server-side meaning.
function withoutHarnessDecoration(spec) {
  const { output, ...rest } = spec;
  if (!output || spec.having?.length || hasHarnessFields(rest)) return null;
  const dimensions = output.dimensions || [];
  const metrics = output.metrics || [];
  const anchors = dimensions.filter(d => ROW_IDS.has(d.field?.field));
  if (!anchors.length) return null;
  const anchored = field => HARNESS_FIELDS.has(field?.field)
    && anchors.some(anchor => anchor.field.stepAlias === field.stepAlias);
  const removedDimensions = dimensions.filter(d => anchored(d.field));
  const removedMetrics = metrics.filter(m => anchored(m.computed?.field)
    && ["AGGREGATE_OP_MAX", "AGGREGATE_OP_COUNT_DISTINCT"].includes(m.computed.op));
  // Removing a GROUP BY field would merge categories even with a row ID.
  if (removedDimensions.length && metrics.length) return null;
  const removed = new Set([...removedDimensions, ...removedMetrics]);
  if (!removed.size) return null;
  const aliases = new Set([...removed].map(item => item.outputAlias));
  if ((spec.orderBy || []).some(order => aliases.has(order.outputAlias))) return null;
  const compatible = {
    ...spec,
    output: {
      ...output,
      ...(output.dimensions ? { dimensions: dimensions.filter(d => !removed.has(d)) } : {}),
      ...(output.metrics ? { metrics: metrics.filter(m => !removed.has(m)) } : {}),
    },
  };
  return hasHarnessFields(compatible) ? null : compatible;
}

export async function runWithOptionalHarness(spec, run) {
  try {
    return await run(spec);
  } catch (error) {
    if (!isUnsupportedHarness(error)) throw error;
    // Older servers cannot name an unknown numeric enum. Let the existing
    // answer-status compatibility path resolve that ambiguity first.
    if (!/DIMENSION_FIELD_\w+_HARNESS(?:_LABEL)?\b/.test(`${error?.responseText || ""}\n${error?.message || ""}`)
      && (spec.output?.dimensions || []).some(d => d.field?.field === "DIMENSION_FIELD_PROMPT_ANSWER_STATUS")) throw error;
    const compatible = withoutHarnessDecoration(spec);
    if (!compatible) throw error;
    return run(compatible);
  }
}
